import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiPhone, FiHome, FiPackage, FiFileText } from "react-icons/fi";
import { CURRENT_USER_DATA_URL } from "../constants";
import AgentChangeRequests from "./AgentChangeRequests";
import AgentAvatar from "./AgentAvatar";
import Loader from "./common/Loader";

const AgentProfile = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const getData = async () => {
    setLoading(true);
    try {
      const response = await fetch(CURRENT_USER_DATA_URL, {
        headers: { "ngrok-skip-browser-warning": true },
      });

      if (!response.ok) throw new Error("Failed to fetch agent data");
      const res = await response.json();
      const agent = res.agentDetail;
      setData({
        agentID: agent.agentID,

        phone: agent.localPhoneNumber,
        fax: agent.fax,
        mobile: agent.mobile,
        email: agent.email,

        localAddressLine1: agent.localAddressLine1,
        localAddressLine2: agent.localAddressLine2,
        localAddressLine3: agent.localAddressLine3,
        localSuburb: agent.lSuburb,
        localState: agent.lState,
        localPostCode: agent.lPostCode,
        localCountry: agent.lCountry,

        mailingAddressLine1: agent.mailingAddressLine1,
        mailingAddressLine2: agent.mailingAddressLine2,
        mailingAddressLine3: agent.mailingAddressLine3,
        mailingSuburb: agent.mSuburb,
        mailingState: agent.mState,
        mailingPostCode: agent.mPostCode,
        mailingCountry: agent.mCountry,
      });
    } catch (error) {
      console.log(error);
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const joinAddress = (parts) =>
    parts
      .map((x) => (typeof x === "string" ? x.trim() : x))
      .filter(Boolean)
      .join(", ");

  const residentialAddress = joinAddress([
    data.localAddressLine1,
    data.localAddressLine2,
    data.localAddressLine3,
    data.localSuburb,
    [data.localState, data.localPostCode].filter(Boolean).join(" ").trim(),
    data.localCountry,
  ]);

  const mailingAddress = joinAddress([
    data.mailingAddressLine1,
    data.mailingAddressLine2,
    data.mailingAddressLine3,
    data.mailingSuburb,
    [data.mailingState, data.mailingPostCode].filter(Boolean).join(" ").trim(),
    data.mailingCountry,
  ]);
  
  if (loading) return <Loader loaded={true} hidden="" />;

  return (
    <>
      <Loader loaded={loading} hidden={loading ? "" : "hidden"} />

      <div className="mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Card */}
          <div className="bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6">
            <div className="flex justify-end">
              <Link
                to="/dashboard/edit-agent-details"
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-white dark:hover:text-gray-300 font-medium"
              >
                Edit
              </Link>
            </div>

            <div className="flex items-center pt-4 gap-6">
              <AgentAvatar size={80} textClass="text-2xl" />

              <div className="space-y-2">
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {localStorage.getItem("agentName") || ""}
                </p>

                {data.email ? (
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <FiMail className="w-4 h-4" />
                    <span className="text-sm">{data.email}</span>
                  </div>
                ) : null}

                {data.mobile || data.phone ? (
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <FiPhone className="w-4 h-4" />
                    <span className="text-sm">{data.mobile || data.phone}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Address Card */}
          <div className="bg-white dark:bg-white/5 text-gray-900 dark:text-gray-100 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6">
            {Boolean(residentialAddress) && (
              <>
                <div className="flex items-center gap-3 pb-1">
                  <div className="w-8 h-8 bg-blue-50 dark:bg-white/10 rounded-lg flex items-center justify-center">
                    <FiHome className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Local Address
                  </h3>
                </div>

                <div className="flex-1 py-0.5 pl-[3.2rem] pb-2 border-b border-gray-100 dark:border-neutral-700">
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                    {residentialAddress}
                  </p>
                </div>
              </>
            )}

            {Boolean(mailingAddress) && (
              <>
                <div
                  className={`flex items-center gap-3 ${
                    Boolean(residentialAddress) ? "mt-4" : ""
                  }`}
                >
                  <div className="w-8 h-8 bg-blue-50 dark:bg-white/10 rounded-lg flex items-center justify-center">
                    <FiPackage className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Mailing Address
                  </h3>
                </div>

                <div className="flex-1 py-1 pl-[3.2rem] pb-2 border-b border-gray-100 dark:border-neutral-700">
                  <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                    {mailingAddress}
                  </p>
                </div>
              </>
            )}

            {!residentialAddress && !mailingAddress && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No address details available
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <AgentChangeRequests />
      </div>
    </>
  );


};

export default AgentProfile;
